const Decimal = require('decimal.js');

// Lifted rows reach the override match as `left_hand - lift_amount`, so exact
// equality drops the override against the amount stored at decimal(15,5).
// Mirrors V3SettlementEditMatcher::AMOUNT_TOLERANCE — keep the values equal.
const AMOUNT_TOLERANCE = 1e-6;

const amountsMatch = (a, b) => Math.abs(parseFloat(a) - parseFloat(b)) < AMOUNT_TOLERANCE;

export class SettlementMath {
  static NET_GROSS_BASIS_CALC_TYPES = ['percentage_of_net_gross', 'tax_inclusive_percentage_of_net_gross'];
  static TAX_INCLUSIVE_CALC_TYPES = ['tax_inclusive_percentage', 'tax_inclusive_percentage_of_net_gross'];
  static PERCENT_CALC_TYPES = ['percentage_of_gross', 'tax_inclusive_percentage', 'percentage_of_net_gross', 'tax_inclusive_percentage_of_net_gross'];
  static GROSS_TICKET_SALES_BASIS = 'gross_ticket_sales';

  static artistPaymentsSection(settlementLineItems) {
    var items = settlementLineItems
      .filter((settlementLineItem) => settlementLineItem.section === "Artist Payments")
      .map((item) => {
        return Object.assign({}, item, {
          overridable_id: item.id,
          overridable_type: "SettlementLineItem",
          calc_type: "fixed",
          calculatedTotal: new Decimal(item.amount || 0).times(item.quantity || 0).toNumber()
        });
      });

    return {
      items: items,
      total: items
        .reduce((summed, item) => summed.plus(item.calculatedTotal), new Decimal(0))
        .toNumber()
    };
  }

  static dueAfterArtistPayments(settlementDue, artistPaymentsTotal) {
    return new Decimal(settlementDue)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .minus(artistPaymentsTotal)
      .toNumber();
  }

  constructor(budget, isSettling, offer) {
    this.isSettling = (isSettling || false);
    this.budget = Object.assign({}, budget);
    this.offer = offer || {};
    this.taxWithholdingMinimumThreshold = this.parseMaskedInput(this.budget.tax_withholding_minimum_threshold) || 0.00;
    this.taxWithholdingPercentage = this.parseMaskedInput(this.budget.tax_withholding_percentage) || 0.00;
  }

  calculate() {
    this.ticketsSection = this.calculateSettlementSection({
      items: this.buildTicketItems()
    });

    this.revenueSection = this.calculateSettlementSection({
      items: this.buildRevenueItems()
    });

    // O-4509: when "apply tax after the facility fee" is on, the offer's tax
    // rows stop being deductions and become a pass-through on the net gate, so
    // they are split out of the before-adjusted section before it is totalled.
    var passThroughTax = !!this.offer.apply_tax_after_facility_fee;
    var beforeAdjustedItems = this.buildBeforeAdjustedDeductionsItems();

    var admissionTaxRows = passThroughTax ? this.selectAdmissionTaxRows(beforeAdjustedItems) : [];
    // Tax already inside the gate keeps deducting — that subtraction is what
    // brings the all-in gate down to face. Only tax outside it is lifted out.
    var liftedRows = new Set(admissionTaxRows.filter((item) => !this.isTaxInsideGate(item)));

    this.beforeAdjustedDeductionsSection = this.calculateSettlementSection({
      items: passThroughTax
        ? beforeAdjustedItems.filter((item) => !liftedRows.has(item))
        : beforeAdjustedItems
    });

    this.admissionTaxSection = this.calculateSettlementSection({ items: admissionTaxRows });

    this.showSummary = this.calculateNetBoxOfficeReceipts();

    this.expensesSection = this.calculateSettlementSection({
      items: this.buildExpensesItems()
    });

    this.showSummary = this.calculateShowNetAfterExpenses();

    this.finalExpensesSection = this.calculateSettlementSection({
      items: this.buildFinalExpensesItems()
    });

    this.showSummary = this.calculateShowSummary();

    this.additionsSection = this.calculateSettlementSection({
      items: this.buildAdditionsItems()
    });

    this.depositsSection = this.calculateSettlementSection({
      items: this.buildDepositItems()
    });

    this.deductionsSection = this.calculateSettlementSection({
      items: this.buildDeductionsItems()
    });

    this.artistPaymentsSection = SettlementMath.artistPaymentsSection(
      this.isSettling ? this.budget.settlement_line_items : []
    );

    this.artistEarnings = this.calculateArtistEarnings();
    this.artistEarningsForecast = this.calculateArtistEarningsForecast();
    this.artistEarningsActual = this.calculateArtistEarningsActual();

    this.showSummary = this.calculateVenueNetPotential();
    this.showSummary = this.calculateVenueNetPotentialForecast();
    this.showSummary = this.calculateVenueNet();

    this.rebaseLiftedTaxRowsForDisplay();

    return this;
  }

  parseMaskedInput(number) {
    if(typeof number === 'string') {
      return parseFloat(number.replace(/,/g, ''));
    } else {
      return parseFloat(number);
    }
  }

  zeroMinimum(number) {
    return number > 0 ? number : 0;
  }

  roundCurrency(number) {
    return new Decimal(number).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  }


  numberOfPaidTickets() {
    return this.ticketsSection.items
      .filter((item) => item.calculatedLeftHand > 0.00)
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedRightHand;
      }, 0);
  }

  numberOfPaidTicketsForecast() {
    return this.ticketsSection.items
      .filter((item) => item.calculatedLeftHandForecast > 0.00)
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedRightHandForecast;
      }, 0);
  }

  buildTicketItems() {
    var items = this.budget.ticket_line_items;

    items = items.map((item) => {
      if(item.lift_amount && item.lift_amount > 0.00) {
        var amountAfterLift = this.zeroMinimum(
          item.left_hand - item.lift_amount
        );

        var amountAfterLiftForecast = this.zeroMinimum(
          item.left_hand_forecast - item.lift_amount
        );

        return Object.assign({}, item, {
          left_hand: amountAfterLift,
          left_hand_forecast: amountAfterLiftForecast
        });
      } else {
        return item;
      }
    });

    if(this.isSettling){
      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Tickets")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Revenue");
  }

  buildRevenueItems() {
    var items = this.budget.income_line_items;

    items = items.map((item) => {
      if(item.lift_amount && item.lift_amount > 0.00) {
        var amountAfterLift = this.zeroMinimum(
          item.left_hand - item.lift_amount
        );

        var amountAfterLiftForecast = this.zeroMinimum(
          item.left_hand_forecast - item.lift_amount
        );

        return Object.assign({}, item, {
          left_hand: amountAfterLift,
          left_hand_forecast: amountAfterLiftForecast
        });
      } else {
        return item;
      }
    });

    if(this.isSettling){
      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Revenue")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Revenue");
  }

  shouldShow(item) {
    if (this.isSettling) {
      return !!item.settlementVisibility
        ? item.settlementVisibility.visible
        : item.show_in_offer;
    } else {
      return item.show_in_offer;
    }
  }

  buildBeforeAdjustedDeductionsItems() {
    var items = this.budget.before_adjusted_deductions_line_items;

    var manualTaxRows = this.budget.ticket_line_items
      .filter((item) => item.taxes && item.taxes > 0.00)
      .filter((item) => this.shouldShow(item))
      .map((item) => {
        var taxDescription = "Taxes";

        if(item.tax_description && item.tax_description.length > 0) {
          taxDescription = item.tax_description;
        }

        return {
          name: `${item.name} - ${taxDescription}`,
          left_hand: item.taxes,
          operator: "*",
          right_hand: item.right_hand,
          show_in_offer: true,
          overridable_id: item.overridable_id,
          overridable_type: "ManualTax",
          calc_type: "fixed",
          included_in_gross: !!this.offer.settlement_price_includes_tax,
          tax_rate: item.tax_rate
        }
      });

    // O-4509: at settlement the tax actually charged on each ticket is the
    // truth. Any tax line carried on the offer — the ManualTax forecast, or one
    // the venue marked by hand — is REPLACED by those rows, never added to
    // them, or the same tax lands twice. With no per-ticket tax charged, the
    // forecast still goes (it only ever forecast manual taxes) while a marked
    // line stays, since for those venues it is the only tax there is.
    if (this.isSettling) {
      items = manualTaxRows.length > 0
        ? items.filter((item) => !this.isAdmissionTaxRow(item))
        : items.filter((item) => item.kind !== 'manual_tax_projection');
    }

    items = items.concat(manualTaxRows);

    if(this.isSettling){
      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Before Adjusted Deductions")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Before Adjusted Deductions");
  }

  buildFinalExpensesItems() {
    var items = this.budget.expense_line_items.filter( item => {
      return item.calc_type === "percentage_of_net"
          || item.calc_type === "percentage_of_expenses";
    });

    return this.postProcessSectionItems(items, "Expenses");
  }

  buildExpensesItems() {
    var items = this.budget.expense_line_items.filter( item => {
      return item.calc_type !== "percentage_of_net"
          && item.calc_type !== "percentage_of_expenses";
    });

    if(this.isSettling){
      items = items.concat(
        (this.budget.cached_sibling_headliner_budgets || [])
          .map((sibling) => {
            var budgetGuarantee = sibling.dollar_amount ?
              this.parseMaskedInput(sibling.dollar_amount) : 0.00;

            return {
              name: sibling.artist_or_event_name,
              left_hand: budgetGuarantee,
              operator: "*",
              right_hand: 1,
              show_in_offer: true,
              overridable_id: sibling.id,
              overridable_type: "Budget",
              calc_type: "fixed"
            }
          })
      );

      items = items.concat(
        this.budget.finance_items
          .filter((fi) => fi.on_settlement)
          .filter((fi) => !fi.is_income)
          .filter((fi) => ["Show Expense", "Show Expense + Buyout"].includes(fi.expense_type))
          .map((fi) => fi.finance_line_items).flat()
          .map((fli) => {
            return {
              name: fli.description,
              left_hand: fli.actual,
              operator: "*",
              right_hand: 1,
              show_in_offer: true,
              overridable_id: fli.id,
              overridable_type: "FinanceLineItem",
              calc_type: "fixed"
            }
          })
      );

      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Expenses")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Expenses");
  }

  buildAdditionsItems() {
    var items = this.budget.addition_line_items;
    var paidTicketsCount = this.numberOfPaidTickets();

    items = items.concat(
      this.budget.budget_bonuses
        .filter((b) => b.ticket_count_requirement)
        .filter((b) => b.dollar_amount)
        .filter((b) => b.dollar_or_percentage_operator === "CASH")
        .filter((b) => {
          var ticketCountRequirement = typeof b.ticket_count_requirement === 'string'
            ? parseFloat(b.ticket_count_requirement.replace(/,/g, ''))
            : b.ticket_count_requirement;

          return ticketCountRequirement <= paidTicketsCount
        })
        .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement)
        .map((b) => {
          return {
            name: "Bonus for " + b.ticket_count_requirement + " tickets",
            left_hand: this.parseMaskedInput(b.dollar_amount),
            operator: "*",
            right_hand: 1,
            show_in_offer: true,
            overridable_id: b.overridable_id,
            overridable_type: b.overridable_type,
            calc_type: "fixed"
          }
        })
    )

    this.budget.is_headliner && (items = items.concat(
      this.budget.ticket_line_items
        .filter((item) => item.lift_amount && item.lift_amount > 0.00)
        .map((item) => {
          var discountedLiftAmount = Math.min(item.lift_amount, item.left_hand);
          var discountedLiftAmountForecast = Math.min(item.lift_amount, item.left_hand_forecast);

          return {
            name: item.name,
            left_hand: discountedLiftAmount,
            left_hand_forecast: discountedLiftAmountForecast,
            operator: "*",
            right_hand: item.right_hand,
            right_hand_forecast: item.right_hand_forecast,
            show_in_offer: true,
            overridable_id: item.overridable_id,
            overridable_type: item.overridable_type,
            calc_type: "fixed",
            lift: true
          }
        })
    ));

    if(this.isSettling){
      items = items.concat(
        this.budget.finance_items
          .filter((fi) => fi.on_settlement)
          .filter((fi) => {
            return (
              fi.is_income
                || fi.expense_type === "Show Expense + Buyout"
            );
          })
          .map((fi) => fi.finance_line_items).flat()
          .map((fli) => {
            return {
              name: fli.description,
              left_hand: fli.actual,
              operator: "*",
              right_hand: 1,
              show_in_offer: true,
              overridable_id: fli.id,
              overridable_type: "FinanceLineItem",
              calc_type: "fixed"
            }
          })
      )

      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Additions")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Additions");
  }

  buildDepositItems() {
    var items = this.budget.deposits
      .filter(deposit => deposit.paid)
      .map(deposit => {
        return {
          name: "Deposit",
          left_hand: deposit.amount,
          operator: "*",
          right_hand: 1,
          show_in_offer: true,
          overridable_id: deposit.id,
          overridable_type: "Deposit",
          calc_type: "fixed"
        }
      });

    this.depositItems = items;

    return this.postProcessSectionItems(items, "Deposits");
  }

  buildDeductionsItems() {
    var items = [];

    if(this.isSettling){
      items = items.concat(this.depositItems);

      items = items.concat(
        this.budget.finance_items
          .filter((fi) => fi.on_settlement)
          .filter((fi) => !fi.is_income)
          .filter((fi) => fi.expense_type === "Deduction")
          .map((fi) => fi.finance_line_items).flat()
          .map((fli) => {
            return {
              name: fli.description,
              left_hand: fli.actual,
              operator: "*",
              right_hand: 1,
              show_in_offer: true,
              overridable_id: fli.id,
              overridable_type: "FinanceLineItem",
              calc_type: "fixed"
            }
          })
      );

      items = items.concat(
        this.budget.settlement_line_items
          .filter((sli) => sli.section === "Deductions")
          .map((item) => {
            return Object.assign({}, item, {
              left_hand: item.amount,
              operator: "*",
              right_hand: item.quantity,
              show_in_offer: true,
              overridable_id: item.id,
              overridable_type: "SettlementLineItem",
              calc_type: "fixed"
            });
          })
      );
    }

    return this.postProcessSectionItems(items, "Deductions");
  }

  calculateSettlementSection(section) {
    var itemsPass1 = section.items
      .map((item) => {
        if (SettlementMath.NET_GROSS_BASIS_CALC_TYPES.includes(item.calc_type) && !this.showSummary?.netBoxOfficeReceipts) {
          return item;
        }
        var calculated = this.calculateLineItem(item, section.items);
        return Object.assign({}, item, calculated);
      });

    var items = itemsPass1.map((item) => {
      if (SettlementMath.NET_GROSS_BASIS_CALC_TYPES.includes(item.calc_type) && !item.calculatedTotal) {
        var calculated = this.calculateLineItem(item, section.items, itemsPass1);
        return Object.assign({}, item, calculated);
      }
      return item;
    });

    var leftTotal = items
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedLeftHand;
      }, 0);

    var rightTotal = items
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedRightHand;
      }, 0);

    var total = items
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedTotal;
      }, 0);

    var leftForecastTotal = items
      .reduce((summed, item) => {
        return summed + item.calculatedLeftHandForecast;
      }, 0);

    var rightForecastTotal = items
      .reduce((summed, item) => {
        return summed + item.calculatedRightHandForecast;
      }, 0);

    var forecastTotal = items
      .reduce((summed, item) => {
        return summed + item.calculatedForecastTotal;
      }, 0);

    var externalForecastTotal = items
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedForecastTotal;
      }, 0);

    var internalTotal = items
      .filter((item) => !this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedTotal;
      }, 0);

    var internalForecastTotal = items
      .filter((item) => !this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedForecastTotal;
      }, 0);

    return {
      items: items,
      leftTotal: leftTotal,
      rightTotal: rightTotal,
      total: total,
      leftForecastTotal: leftForecastTotal,
      rightForecastTotal: rightForecastTotal,
      forecastTotal: forecastTotal,
      externalForecastTotal: externalForecastTotal,
      internalTotal: internalTotal,
      internalForecastTotal: internalForecastTotal
    }
  }

  calculateLineItem(item, sectionItems = [], calculatedItems = []) {
    let calculatedTotal;
    let calculatedForecastTotal;

    let calculatedLeftHand = (
      item.left_hand ? this.parseMaskedInput(item.left_hand) : 0.00
    );

    let calculatedLeftHandForecast = (
      item.left_hand_forecast ? this.parseMaskedInput(item.left_hand_forecast) : 0.00
    );

    let calculatedRightHand;
    let calculatedRightHandForecast;

    switch(item.calc_type) {
      case "fixed":
        calculatedRightHand = item.right_hand ? this.parseMaskedInput(item.right_hand) : 0.00;
        calculatedRightHandForecast = item.right_hand_forecast ? this.parseMaskedInput(item.right_hand_forecast) : 0.00;
        break;
      case "per_tickets_sold":
        calculatedRightHand = this.numberOfPaidTickets();
        calculatedRightHandForecast = this.numberOfPaidTicketsForecast();
        break;
      case "percentage_of_gross":
      case "tax_inclusive_percentage":
        calculatedRightHand = this.ticketsSection.total + this.revenueSection.total;
        calculatedRightHandForecast = this.ticketsSection.externalForecastTotal + this.revenueSection.externalForecastTotal;
        break;
      case "percentage_of_net_gross":
      case "tax_inclusive_percentage_of_net_gross":
        // Prioritize showSummary.netBoxOfficeReceipts when available (for expenses)
        // Only use calculatedItems logic when showSummary doesn't exist yet (for beforeAdjustedDeductions)
        if (this.showSummary && this.showSummary.netBoxOfficeReceipts !== undefined) {
          calculatedRightHand = this.showSummary.netBoxOfficeReceipts;
          calculatedRightHandForecast = this.showSummary.netBoxOfficeReceiptsForecast;
        } else if (calculatedItems.length > 0) {
          var gross = this.ticketsSection.total + this.revenueSection.total;
          var grossForecast = this.ticketsSection.externalForecastTotal + this.revenueSection.externalForecastTotal;

          var otherDeductions = calculatedItems
            .filter(i => i.uuid !== item.uuid && !SettlementMath.NET_GROSS_BASIS_CALC_TYPES.includes(i.calc_type) && i.calculatedTotal !== undefined)
            .reduce((sum, i) => sum + i.calculatedTotal, 0);

          var otherDeductionsForecast = calculatedItems
            .filter(i => i.uuid !== item.uuid && !SettlementMath.NET_GROSS_BASIS_CALC_TYPES.includes(i.calc_type) && i.calculatedForecastTotal !== undefined)
            .reduce((sum, i) => sum + i.calculatedForecastTotal, 0);

          calculatedRightHand = gross - otherDeductions;
          calculatedRightHandForecast = grossForecast - otherDeductionsForecast;
        } else {
          calculatedRightHand = this.ticketsSection.total + this.revenueSection.total;
          calculatedRightHandForecast = this.ticketsSection.forecastTotal + this.revenueSection.forecastTotal;
        }
        break;
      case "percentage_of_net":
        calculatedRightHand = this.showSummary.showNetAfterExpenses;
        calculatedRightHandForecast = this.showSummary.showNetAfterExpensesForecast;
        break;
      case "percentage_of_expenses":
        calculatedRightHand = this.expensesSection.total;
        calculatedRightHandForecast = this.expensesSection.externalForecastTotal;
        break;
      default:
        throw `Unknown calc_type (${item.calc_type}) while calculating line item total`;
    }

    if(item.settlementOverride) {
      calculatedRightHand = this.parseMaskedInput(item.right_hand);
    }

    if (SettlementMath.TAX_INCLUSIVE_CALC_TYPES.includes(item.calc_type)) {
      const divisor = 1 + (calculatedLeftHand / 100);
      const divisorForecast = 1 + (calculatedLeftHandForecast / 100);
      calculatedTotal = divisor === 0 ? 0 :
        new Decimal(calculatedRightHand).minus(new Decimal(calculatedRightHand).div(divisor))
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
      calculatedForecastTotal = divisorForecast === 0 ? 0 :
        new Decimal(calculatedRightHandForecast).minus(new Decimal(calculatedRightHandForecast).div(divisorForecast))
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();

      if (item.max_value !== null && item.max_value !== undefined) {
        const maxValue = this.parseMaskedInput(item.max_value);
        if (maxValue > 0 && calculatedTotal > maxValue) calculatedTotal = maxValue;
        if (maxValue > 0 && calculatedForecastTotal > maxValue) calculatedForecastTotal = maxValue;
      }
      if (item.min_value !== null && item.min_value !== undefined) {
        const minValue = this.parseMaskedInput(item.min_value);
        if (minValue > 0 && calculatedTotal < minValue) calculatedTotal = minValue;
        if (minValue > 0 && calculatedForecastTotal < minValue) calculatedForecastTotal = minValue;
      }

      return {
        calculatedLeftHand: calculatedLeftHand,
        calculatedRightHand: calculatedRightHand,
        calculatedTotal: calculatedTotal,
        calculatedLeftHandForecast: calculatedLeftHandForecast,
        calculatedRightHandForecast: calculatedRightHandForecast,
        calculatedForecastTotal: calculatedForecastTotal
      };
    }

    switch(item.operator) {
      case "+":
        calculatedTotal = (calculatedLeftHand + calculatedRightHand);
        calculatedForecastTotal = (calculatedLeftHandForecast + calculatedRightHandForecast);
        break;
      case "-":
        calculatedTotal = (calculatedLeftHand - calculatedRightHand);
        calculatedForecastTotal = (calculatedLeftHandForecast - calculatedRightHandForecast);
        break;
      case "*":
        calculatedTotal = (calculatedLeftHand * calculatedRightHand);
        calculatedForecastTotal = (calculatedLeftHandForecast * calculatedRightHandForecast);
        break;
      case "of":
        calculatedTotal = new Decimal(calculatedLeftHand).mul(calculatedRightHand).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
        calculatedForecastTotal = new Decimal(calculatedLeftHandForecast).mul(calculatedRightHandForecast).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
        break;
      case "/":
        calculatedTotal = (calculatedLeftHand / calculatedRightHand);
        calculatedForecastTotal = (calculatedLeftHandForecast / calculatedRightHandForecast);
        break;
      default:
        throw "Unknown operator while calculating line item total";
    }

    if (item.max_value !== null && item.max_value !== undefined) {
      var maxValue = this.parseMaskedInput(item.max_value);
      if (maxValue > 0 && calculatedTotal > maxValue) {
        calculatedTotal = maxValue;
      }
      if (maxValue > 0 && calculatedForecastTotal > maxValue) {
        calculatedForecastTotal = maxValue;
      }
    }

    if (item.min_value !== null && item.min_value !== undefined) {
      var minValue = this.parseMaskedInput(item.min_value);
      if (minValue > 0 && calculatedTotal < minValue) {
        calculatedTotal = minValue;
      }
      if (minValue > 0 && calculatedForecastTotal < minValue) {
        calculatedForecastTotal = minValue;
      }
    }

    return {
      calculatedLeftHand: calculatedLeftHand,
      calculatedRightHand: calculatedRightHand,
      calculatedTotal: calculatedTotal,
      calculatedLeftHandForecast: calculatedLeftHandForecast,
      calculatedRightHandForecast: calculatedRightHandForecast,
      calculatedForecastTotal: calculatedForecastTotal
    };
  }

  calculateNetBoxOfficeReceipts() {
    var netBoxOfficeReceipts = (
      this.ticketsSection.total
        + this.revenueSection.total
        - this.beforeAdjustedDeductionsSection.total
    );

    var netBoxOfficeReceiptsForecast = (
      this.ticketsSection.externalForecastTotal
        + this.revenueSection.externalForecastTotal
        - this.beforeAdjustedDeductionsSection.externalForecastTotal
    );

    return {
      ...this.showSummary,
      netBoxOfficeReceipts: netBoxOfficeReceipts,
      netBoxOfficeReceiptsForecast: netBoxOfficeReceiptsForecast,
      admissionTax: this.admissionTax('offer'),
      admissionTaxForecast: this.admissionTax('forecast'),
    }
  }

  // Mirrors FinancialSummaryMath#rebaseLiftedTaxRowsForDisplay: a lifted tax row
  // is charged on the net gate, so its own figures must match the pass-through
  // line rather than reporting the tax on gross.
  rebaseLiftedTaxRowsForDisplay() {
    var items = (this.admissionTaxSection && this.admissionTaxSection.items) || [];
    var lifted = items.filter((i) => !this.isTaxInsideGate(i));
    if (lifted.length === 0) return;

    var modes = [
      ['calculatedTotal', 'calculatedRightHand', this.showSummary.admissionTax, this.showSummary.netBoxOfficeReceipts],
      ['calculatedForecastTotal', 'calculatedRightHandForecast', this.showSummary.admissionTaxForecast, this.showSummary.netBoxOfficeReceiptsForecast],
    ];

    modes.forEach((m) => {
      var totalKey = m[0], rightKey = m[1], target = m[2], netGate = m[3];
      var rawTotal = lifted.reduce((sum, i) => sum + (i[totalKey] || 0), 0);
      lifted.forEach((item) => {
        var share = rawTotal > 0
          ? new Decimal(item[totalKey] || 0).dividedBy(rawTotal)
          : new Decimal(1).dividedBy(lifted.length);
        item[totalKey] = new Decimal(target || 0).times(share)
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
        if (SettlementMath.PERCENT_CALC_TYPES.indexOf(item.calc_type) >= 0) item[rightKey] = netGate || 0;
      });
    });

    var sum = (key) => items.reduce((total, i) => total + (i[key] || 0), 0);
    this.admissionTaxSection.total = sum('calculatedTotal');
    this.admissionTaxSection.forecastTotal = sum('calculatedForecastTotal');
  }

  // A before-adjusted-deduction row that represents admission/sales tax. Mirrors
  // FinancialSummaryMath#isAdmissionTaxRow — the two engines must agree on what
  // counts as tax. Facility fee and charity are explicitly never tax. (O-4509)
  // Mirrors FinancialSummaryMath#isAdmissionTaxRow: explicitly marked rows and
  // real ManualTax rows only, never inferred from calc_type. The two engines
  // must agree on what counts as tax.
  isAdmissionTaxRow(item) {
    if (!item) return false;
    if (item.kind === 'facility_maintenance_fee' || item.kind === 'charity_deduction') return false;
    return item.kind === 'admission_tax'
      || item.kind === 'manual_tax_projection'
      || item.overridable_type === 'ManualTax';
  }

  selectAdmissionTaxRows(rows) {
    return rows.filter((item) => this.isAdmissionTaxRow(item));
  }

  // Settlement grouped the ticket price with the tax inside it, so the gate
  // already contains this tax. Mirrors FinancialSummaryMath.
  isTaxInsideGate(item) {
    return !!item && item.included_in_gross === true;
  }

  // Combined rate across the tax rows, or null when any row doesn't know its
  // rate. Mirrors FinancialSummaryMath.
  admissionTaxRate(rows) {
    rows = rows || (this.admissionTaxSection && this.admissionTaxSection.items) || [];
    if (rows.length === 0) return null;
    var total = new Decimal(0);
    for (var i = 0; i < rows.length; i++) {
      var rate = parseFloat(rows[i].tax_rate);
      if (!rate || rate <= 0) return null;
      total = total.plus(rate);
    }
    return total;
  }

  taxInsideGateTotal(mode) {
    var totalKey = mode === 'forecast' ? 'externalForecastTotal' : 'total';
    var rows = (this.admissionTaxSection && this.admissionTaxSection.items || [])
      .filter((item) => this.isTaxInsideGate(item));
    if (rows.length === 0) return new Decimal(0);
    return new Decimal(this.calculateSettlementSection({ items: rows })[totalKey] || 0);
  }

  facilityMaintenanceFeeTotal(mode) {
    var key = mode === 'forecast' ? 'calculatedForecastTotal' : 'calculatedTotal';
    var items = (this.beforeAdjustedDeductionsSection && this.beforeAdjustedDeductionsSection.items) || [];

    return items.reduce((summed, item) => {
      var isFacilityFee = item.kind === 'facility_maintenance_fee'
        || item.name === 'Facilities Maintenance Fee';

      if (!isFacilityFee) return summed;
      if (!this.shouldShow(item)) return summed;

      return summed.plus(new Decimal(item[key] || 0));
    }, new Decimal(0));
  }

  admissionTax(mode) {
    if (!this.offer.apply_tax_after_facility_fee) return 0;

    var totalKey = mode === 'forecast' ? 'forecastTotal' : 'total';

    // Tax inside the gate passes through at exactly what was collected —
    // re-basing tax the buyer already paid would report a figure nobody was
    // charged. Only tax outside the gate is charged on the net gate per the
    // deal terms, so only that part is ever re-based. (O-4509)
    var insideTotal = this.taxInsideGateTotal(mode);
    var liftedRows = ((this.admissionTaxSection && this.admissionTaxSection.items) || [])
      .filter((i) => !this.isTaxInsideGate(i));

    var lifted = new Decimal(0);
    if (liftedRows.length > 0) {
      var liftedRaw = new Decimal(this.calculateSettlementSection({ items: liftedRows })[totalKey] || 0);
      if (liftedRaw.greaterThan(0)) {
        var gross = new Decimal(this.grossPotentialForMode(mode)).minus(insideTotal);
        if (gross.greaterThan(0)) {
          // A facility fee larger than the gate would otherwise produce a
          // negative pass-through; there is no such thing as negative tax.
          var netGate = Decimal.max(gross.minus(this.facilityMaintenanceFeeTotal(mode)), 0);
          // The deal is written against the rate, so prefer it over the
          // collected amount, whose per-ticket rounding drifts.
          var rate = this.admissionTaxRate(liftedRows);
          lifted = rate
            ? netGate.times(rate).dividedBy(100)
            : liftedRaw.times(netGate).dividedBy(gross);
        }
      }
    }

    return insideTotal.plus(lifted).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  }

  calculateShowNetAfterExpenses() {
    var showNetAfterExpenses = (
      this.showSummary.netBoxOfficeReceipts
        - this.expensesSection.total
    );

    var showNetAfterExpensesForecast = (
      this.showSummary.netBoxOfficeReceiptsForecast
        - this.expensesSection.externalForecastTotal
    );

    return {
      ...this.showSummary,
      showNetAfterExpenses: showNetAfterExpenses,
      showNetAfterExpensesForecast: showNetAfterExpensesForecast
    }
  }

  calculateDealBracketAmounts(useForecast = false) {
    const sortedDealBrackets = this.budget.deal_brackets
      .slice()
      .sort((a, b) => a.position - b.position);

    const ticketRows = this.dealBracketTicketRows(useForecast);
    const allocations = this.budget.deal_bracket_basis === SettlementMath.GROSS_TICKET_SALES_BASIS
      ? this.grossBracketAllocations(sortedDealBrackets, ticketRows)
      : this.ticketCountBracketAllocations(sortedDealBrackets, ticketRows);

    const aggregateBracketRevenue = allocations.reduce((sum, a) => sum + a.revenue, 0.00);
    const breakevenScale = this.breakevenScaleFor(aggregateBracketRevenue);

    // Recorded so the "split point met/short" badge can report against the base
    // the gate actually used, rather than recomputing a different one in JSX.
    this.bracketAggregateByMode = this.bracketAggregateByMode || {};
    this.bracketAggregateByMode[useForecast ? 'forecast' : 'offer'] = aggregateBracketRevenue;

    return sortedDealBrackets.map((bracket, index) => {
      const bracketDollarAmount = bracket.dollar_amount ? this.parseMaskedInput(bracket.dollar_amount) : 0.00;
      const bracketUpsidePercentage = bracket.upside_percentage ? this.parseMaskedInput(bracket.upside_percentage) : 0.00;

      let totalGuarantee = 0.00;
      let totalPercentage = 0.00;

      if (allocations[index].paysTerms) {
        const gatedRevenue = allocations[index].revenue * breakevenScale;
        const bracketPercentage = gatedRevenue * (bracketUpsidePercentage / 100.0);

        if (bracket.dollar_or_percentage_operator === 'OR') {
          if (bracketDollarAmount > bracketPercentage) {
            totalGuarantee = bracketDollarAmount;
            totalPercentage = 0.00;
          } else {
            totalGuarantee = 0.00;
            totalPercentage = bracketPercentage;
          }
        } else {
          totalGuarantee = bracketDollarAmount;
          totalPercentage = bracketPercentage;
        }
      }

      return {
        ...bracket,
        totalGuarantee: totalGuarantee,
        totalPercentage: totalPercentage
      };
    });
  }

  dealBracketTicketRows(useForecast) {
    return this.ticketsSection.items
      .filter((item) => item.calculatedLeftHand > 0.00)
      .filter((item) => this.shouldShow(item))
      .map((item) => ({
        quantity: useForecast ? (item.calculatedRightHandForecast || 0) : (item.calculatedRightHand || 0),
        price: useForecast ? (item.calculatedLeftHandForecast || 0.00) : (item.calculatedLeftHand || 0.00)
      }));
  }

  ticketCountBracketAllocations(sortedDealBrackets, ticketRows) {
    const soldTickets = ticketRows.flatMap((row) => Array(row.quantity).fill(row.price));

    return sortedDealBrackets.map((bracket) => {
      const paysTerms = soldTickets.length > 0;
      if (!paysTerms) return { paysTerms: paysTerms, revenue: 0.00 };

      const allocatedTickets = soldTickets.splice(0, Math.min(bracket.quantity || 0, soldTickets.length));
      return {
        paysTerms: paysTerms,
        revenue: allocatedTickets.reduce((sum, price) => sum + price, 0.00)
      };
    });
  }

  grossBracketAllocations(sortedDealBrackets, ticketRows) {
    let remaining = ticketRows
      .reduce((sum, row) => sum.plus(new Decimal(row.price).times(row.quantity)), new Decimal(0))
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

    return sortedDealBrackets.map((bracket) => {
      const width = new Decimal(this.parseMaskedInput(bracket.gross_amount) || 0).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      const allocated = Decimal.min(width, remaining);
      remaining = remaining.minus(allocated);
      return { paysTerms: allocated.greaterThan(0), revenue: allocated.toNumber() };
    });
  }

  calculateShowSummary() {
    var dealBracketResults = null;
    var dealBracketResultsForecast = null;

    if (this.usesDealBrackets()) {
      dealBracketResults = this.calculateDealBracketAmounts(false);
      dealBracketResultsForecast = this.calculateDealBracketAmounts(true);
    }

    this.bracketResultsByMode = {
      offer: dealBracketResults,
      forecast: dealBracketResultsForecast
    };

    var showNetAfterFinalExpenses = (
      this.showSummary.showNetAfterExpenses
        - this.finalExpensesSection.total
    );

    var showNetAfterFinalExpensesForecast = (
      this.showSummary.showNetAfterExpensesForecast
        - this.finalExpensesSection.externalForecastTotal
    );

    var artistGuarantee;
    if(!this.usesDealBrackets()){
      artistGuarantee = this.budget.dollar_amount ? this.parseMaskedInput(this.budget.dollar_amount) : 0.00;
    } else {
      artistGuarantee = dealBracketResults.reduce((total, bracket) => total + bracket.totalGuarantee, 0.00);
    }

    var promoterProfit = 0.00;
    if(this.budget.promoter_profit) {
      var allExpenses = this.expensesSection.total + this.finalExpensesSection.total + artistGuarantee;
      promoterProfit = Math.round(allExpenses * this.budget.promoter_profit_percentage) / 100.0;
    }

    var promoterProfitForecast = 0.00;
    if(this.budget.promoter_profit) {
      var allExpensesForecast = this.expensesSection.externalForecastTotal + this.finalExpensesSection.externalForecastTotal + artistGuarantee;
      promoterProfitForecast = Math.round(allExpensesForecast * this.budget.promoter_profit_percentage) / 100.0;
    }

    var showNetAfterPromoterProfit = (
      showNetAfterFinalExpenses
        - promoterProfit
    );

    var showNetAfterPromoterProfitForecast = (
      showNetAfterFinalExpensesForecast
        - promoterProfitForecast
    );

    var splitPoint = (
      artistGuarantee
        + this.expensesSection.total
        + this.finalExpensesSection.total
        + promoterProfit
    );

    var splitPointForecast = (
      artistGuarantee
        + this.expensesSection.externalForecastTotal
        + this.finalExpensesSection.externalForecastTotal
        + promoterProfitForecast
    );

    var subtractFromShowNet = (
      this.budget.dollar_or_percentage_operator === 'AND' ?
        artistGuarantee : 0.00
    );

    var toBeSharedAmount = this.zeroMinimum(
      showNetAfterPromoterProfit
        - subtractFromShowNet
    );

    var toBeSharedAmountForecast = this.zeroMinimum(
      showNetAfterPromoterProfitForecast
        - subtractFromShowNet
    );

    var upsidePercentage =
      this.finalUpsidePercentage(this.numberOfPaidTickets());

    var revenueBaseOffer = this.getRevenueBase('offer');
    var revenueBaseForecast = this.getRevenueBase('forecast');
    // O-4892: with a Manual Split Point and the default revenue base, the
    // percentage base flips from to-be-shared to gross potential before the
    // split point is subtracted (mirrors FinancialSummaryMath's pool path).
    var breakevenSet = this.breakevenThreshold() != null && this.breakevenThreshold() > 0;
    var artistPercentageBase = revenueBaseOffer !== null
      ? revenueBaseOffer
      : (breakevenSet ? this.grossPotentialForMode('offer') : toBeSharedAmount);
    var artistPercentageBaseForecast = revenueBaseForecast !== null
      ? revenueBaseForecast
      : (breakevenSet ? this.grossPotentialForMode('forecast') : toBeSharedAmountForecast);

    var artistPercentage;
    if(!this.usesDealBrackets()){
      artistPercentage = (upsidePercentage / 100.0) * this.applyBreakevenGate(artistPercentageBase)
    } else {
      artistPercentage = dealBracketResults.reduce((total, bracket) => total + bracket.totalPercentage, 0.00);
    }

    var upsidePercentageForecast =
      this.finalUpsidePercentage(this.numberOfPaidTicketsForecast());

    var artistPercentageForecast;
    if(!this.usesDealBrackets()){
      artistPercentageForecast = (upsidePercentageForecast / 100.0) * this.applyBreakevenGate(artistPercentageBaseForecast);
    } else {
      artistPercentageForecast = dealBracketResultsForecast.reduce((total, bracket) => total + bracket.totalPercentage, 0.00);
    }

    return {
      ...this.showSummary,
      expenses: this.expensesSection.total,
      showNetAfterFinalExpenses: showNetAfterFinalExpenses,
      showNetAfterFinalExpensesForecast: showNetAfterFinalExpensesForecast,
      promoterProfit: promoterProfit,
      promoterProfitForecast: promoterProfitForecast,
      showNetAfterPromoterProfit: showNetAfterPromoterProfit,
      showNetAfterPromoterProfitForecast: showNetAfterPromoterProfitForecast,
      splitPoint: splitPoint,
      splitPointForecast: splitPointForecast,
      toBeSharedAmount: toBeSharedAmount,
      toBeSharedAmountForecast: toBeSharedAmountForecast,
      artistGuarantee: artistGuarantee,
      artistPercentage: artistPercentage,
      artistPercentageForecast: artistPercentageForecast,
      upsidePercentage: upsidePercentage,
      breakevenThreshold: this.breakevenThreshold(),
      breakevenBase: this.breakevenBaseFor('offer', artistPercentageBase),
      breakevenToBeSharedAmount: !this.usesDealBrackets() && this.breakevenThreshold() != null
        ? this.applyBreakevenGate(artistPercentageBase)
        : null,
      breakevenBaseForecast: this.breakevenBaseFor('forecast', artistPercentageBaseForecast)
    }
  }

  breakevenBaseFor(mode, percentageBase) {
    if (!this.usesDealBrackets()) return percentageBase;
    return (this.bracketAggregateByMode || {})[mode] || 0.00;
  }

  usesDealBrackets() {
    if (this.budget.from_dollar_one) return false;
    if ((this.budget.deal_brackets || []).length > 0) return true;
    return !this.budget.flat_terms_without_brackets;
  }

  calculateArtistEarnings() {
    var rawPayoutDecimal;

    if(this.budget.dollar_or_percentage_operator === 'AND') {
      rawPayoutDecimal = new Decimal(this.showSummary.artistGuarantee)
        .plus(this.showSummary.artistPercentage);
    } else {
      rawPayoutDecimal = Decimal.max(
        new Decimal(this.showSummary.artistGuarantee),
        new Decimal(this.showSummary.artistPercentage)
      );
    }

    var clamped = this.applyCapAndFloor(rawPayoutDecimal, 'offer');
    var artistGuaranteeAndOrPercentage = clamped.value.toNumber();

    var rawArtistTotalDue = clamped.value
      .plus(this.additionsSection.total)
      .minus(this.deductionsSection.total)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber();
    this.artistTotalDue = this.zeroMinimum(rawArtistTotalDue);

    this.taxWithholding = 0.00;

    if (this.budget.has_tax_withholding) {
      var depositsTotal = this.isSettling ? this.depositsSection.total : 0;
      var exemptPaid = Math.min(
        this.exemptFromTaxWithholdingTotal('offer'),
        this.zeroMinimum(rawArtistTotalDue + depositsTotal)
      );
      var taxableArtistIncome = this.zeroMinimum(this.artistTotalDue + depositsTotal - exemptPaid);

      if (taxableArtistIncome > this.taxWithholdingMinimumThreshold) {
        this.taxWithholding = taxableArtistIncome * (this.taxWithholdingPercentage / 100.0);
      }
    }

    this.ticketsToBreakeven = this.calculateTicketsToBreakeven();

    var artistSettlementDue = this.artistTotalDue - this.taxWithholding;

    return {
      artistGuaranteeAndOrPercentage: artistGuaranteeAndOrPercentage,
      rawPayout: clamped.rawPayout,
      payoutCapped: clamped.payoutCapped,
      payoutFloored: clamped.payoutFloored,
      capOverflow: clamped.capOverflow,
      capOverflowTarget: clamped.capOverflowTarget,
      totalAdditions: this.additionsSection.total,
      totalDeductions: this.deductionsSection.total,
      taxWithholding: this.taxWithholding,
      artistTotalDue: this.artistTotalDue,
      artistSettlementDue: artistSettlementDue,
      totalArtistPayments: this.artistPaymentsSection.total,
      artistDueAfterPayments: SettlementMath.dueAfterArtistPayments(artistSettlementDue, this.artistPaymentsSection.total),
      ticketsToBreakeven: this.ticketsToBreakeven.averagedBreakeven
    }
  }

  exemptFromTaxWithholdingTotal(mode) {
    var totalKey = mode === 'forecast' ? 'calculatedForecastTotal' : 'calculatedTotal';
    return this.additionsSection.items
      .filter((item) => item.exempt_from_tax_withholding && this.shouldShow(item))
      .reduce((summed, item) => summed + Math.max(0, item[totalKey] || 0), 0);
  }

  applyCapAndFloor(rawPayoutDecimal, mode) {
    var rawPayout = rawPayoutDecimal.toNumber();
    var floor = this.budget.artist_payout_floor != null
      ? this.parseMaskedInput(this.budget.artist_payout_floor)
      : null;
    var cap = this.budget.artist_payout_cap != null
      ? this.parseMaskedInput(this.budget.artist_payout_cap)
      : null;

    var value = rawPayoutDecimal;
    var payoutFloored = false;
    var payoutCapped = false;

    if (floor !== null && !isNaN(floor) && value.lessThan(floor)) {
      value = new Decimal(floor);
      payoutFloored = true;
    }
    if (cap !== null && !isNaN(cap) && value.greaterThan(cap)) {
      value = new Decimal(cap);
      payoutCapped = true;
    }

    var capOverflow = 0;
    var capOverflowTarget = null;
    if (payoutCapped && cap !== null) {
      capOverflow = rawPayoutDecimal.minus(cap).toNumber();
      var brackets = (this.bracketResultsByMode && this.bracketResultsByMode[mode]) || null;
      if (brackets && brackets.length > 0) {
        var cumulative = new Decimal(0);
        for (var i = 0; i < brackets.length; i++) {
          var b = brackets[i];
          cumulative = cumulative.plus(b.totalGuarantee || 0).plus(b.totalPercentage || 0);
          if (cumulative.greaterThan(cap)) {
            capOverflowTarget = b.cap_overflow_target || null;
            break;
          }
        }
      }
    }

    return {
      value: value,
      rawPayout: rawPayout,
      payoutFloored: payoutFloored,
      payoutCapped: payoutCapped,
      capOverflow: capOverflow,
      capOverflowTarget: capOverflowTarget
    };
  }

  calculateArtistEarningsForecast() {
    var rawPayoutDecimal;

    if(this.budget.dollar_or_percentage_operator === 'AND') {
      rawPayoutDecimal = new Decimal(this.showSummary.artistGuarantee)
        .plus(this.showSummary.artistPercentageForecast);
    } else {
      rawPayoutDecimal = Decimal.max(
        new Decimal(this.showSummary.artistGuarantee),
        new Decimal(this.showSummary.artistPercentageForecast)
      );
    }

    var clamped = this.applyCapAndFloor(rawPayoutDecimal, 'forecast');
    var artistGuaranteeAndOrPercentage = clamped.value.toNumber();

    var rawArtistTotalDueForecast = clamped.value
      .plus(this.additionsSection.externalForecastTotal)
      .minus(this.deductionsSection.externalForecastTotal)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber();
    this.artistTotalDueForecast = this.zeroMinimum(rawArtistTotalDueForecast);

    this.taxWithholdingForecast = 0.00;

    if (this.budget.has_tax_withholding) {
      var depositsForecastTotal = this.isSettling ? this.depositsSection.forecastTotal : 0;
      var exemptPaidForecast = Math.min(
        this.exemptFromTaxWithholdingTotal('forecast'),
        this.zeroMinimum(rawArtistTotalDueForecast + depositsForecastTotal)
      );
      var taxableArtistIncomeForecast = this.zeroMinimum(
        this.artistTotalDueForecast + depositsForecastTotal - exemptPaidForecast
      );

      if (taxableArtistIncomeForecast > this.taxWithholdingMinimumThreshold) {
        this.taxWithholdingForecast = taxableArtistIncomeForecast * (this.taxWithholdingPercentage / 100.0);
      }
    }

    this.ticketsToBreakevenForecast = this.calculateTicketsToBreakevenForecast();

    return {
      artistGuaranteeAndOrPercentage: artistGuaranteeAndOrPercentage,
      rawPayout: clamped.rawPayout,
      payoutCapped: clamped.payoutCapped,
      payoutFloored: clamped.payoutFloored,
      capOverflow: clamped.capOverflow,
      capOverflowTarget: clamped.capOverflowTarget,
      totalAdditions: this.additionsSection.externalForecastTotal,
      totalDeductions: this.deductionsSection.externalForecastTotal,
      taxWithholding: this.taxWithholdingForecast,
      artistTotalDue: this.artistTotalDueForecast,
      artistSettlementDue: this.artistTotalDueForecast - this.taxWithholdingForecast,
      ticketsToBreakeven: this.ticketsToBreakevenForecast.averagedBreakeven
    }
  }

  calculateArtistEarningsActual() {
    if (!this.isSettling) return null;
    return this.artistEarnings;
  }

  calculateAncillaryAmounts() {
    var ancillaryIncome = (this.revenueSection.internalTotal || 0) + (this.ticketsSection.internalTotal || 0);
    var ancillaryIncomeForecast = (
      (this.revenueSection.internalForecastTotal || 0) + (this.ticketsSection.internalForecastTotal || 0)
    );
    var ancillaryExpenses = (
      (this.expensesSection.internalTotal || 0) + (this.finalExpensesSection.internalTotal || 0)
    );
    var ancillaryExpensesForecast = (
      (this.expensesSection.internalForecastTotal || 0) + (this.finalExpensesSection.internalForecastTotal || 0)
    );

    return {
      ancillaryIncome: ancillaryIncome,
      ancillaryIncomeForecast: ancillaryIncomeForecast,
      ancillaryExpenses: ancillaryExpenses,
      ancillaryExpensesForecast: ancillaryExpensesForecast,
      ancillaryNet: ancillaryIncome - ancillaryExpenses,
      ancillaryNetForecast: ancillaryIncomeForecast - ancillaryExpensesForecast
    }
  }

  calculateVenueNetPotential() {
    var ancillary = this.calculateAncillaryAmounts();
    var venueNetPotential = (
      this.showSummary.showNetAfterFinalExpenses
        - this.artistEarnings.artistTotalDue
        + ancillary.ancillaryNet
    );

    return {
      ...this.showSummary,
      ...ancillary,
      venueNetPotential: venueNetPotential
    }
  }

  calculateVenueNetPotentialForecast() {
    var ancillary = this.calculateAncillaryAmounts();
    var venueNetPotentialForecast = (
      this.showSummary.showNetAfterFinalExpensesForecast
        - this.artistEarningsForecast.artistTotalDue
        + ancillary.ancillaryNetForecast
    );

    return {
      ...this.showSummary,
      ...ancillary,
      venueNetPotentialForecast: venueNetPotentialForecast
    }
  }

  calculateVenueNet() {
    var shownTotal = (items) => items
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => summed.plus(item.calculatedTotal || 0), new Decimal(0));
    var depositsPaid = shownTotal(this.deductionsSection.items.filter((item) => item.overridable_type === "Deposit"));
    var liftsPassedThrough = shownTotal(this.additionsSection.items.filter((item) => item.lift));

    var artistPayout = Decimal.max(
      new Decimal(this.artistEarnings.artistGuaranteeAndOrPercentage)
        .plus(this.artistEarnings.totalAdditions)
        .minus(this.artistEarnings.totalDeductions)
        .plus(depositsPaid)
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP),
      0
    ).minus(liftsPassedThrough);
    var venueNetExcludingAncillary = new Decimal(this.showSummary.showNetAfterFinalExpenses)
      .minus(artistPayout)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

    return {
      ...this.showSummary,
      expensesAndArtistPayout: new Decimal(this.showSummary.netBoxOfficeReceipts)
        .minus(venueNetExcludingAncillary)
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
        .toNumber() || 0,
      venueNetExcludingAncillary: venueNetExcludingAncillary.toNumber() || 0
    }
  }

  finalUpsidePercentage(paidTicketsCount) {
    var qualifyingBonus = this.budget.budget_bonuses
      .filter((b) => b.upside_percentage)
      .filter((b) => b.ticket_count_requirement)
      .filter((b) => b.dollar_or_percentage_operator === "PERCENTAGE")
      .filter((b) => b.ticket_count_requirement <= paidTicketsCount)
      .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement)
      .pop();

    if(qualifyingBonus) {
      return this.parseMaskedInput(qualifyingBonus.upside_percentage);
    } else {
      return this.budget.upside_percentage
        ? this.parseMaskedInput(this.budget.upside_percentage) : 0.00;
    }
  }

  // V2 settlement-edit matching. Parallel copies live server-side in
  // V3SettlementEditMatcher (V3Budgets::Base) and client-side in the V3 offer
  // preview at V3OfferPreview/applySettlementEdits.js — keep the three in sync.
  postProcessSectionItems(items, section) {
    return items.map((item) => {
      item = Object.assign({}, item, {
        section: section
      });

      if (this.isSettling) {
        var settlementOverride = this.budget.settlement_overrides
          .find((o) => {
            return (
              o.overridable_id === item.overridable_id
                && o.overridable_type === item.overridable_type
                && o.section === item.section
                && amountsMatch(o.amount, item.left_hand)
            );
          });

        settlementOverride && (item = Object.assign({}, item, {
          name: settlementOverride.new_name,
          left_hand: parseFloat(settlementOverride.new_amount),
          right_hand: settlementOverride.new_quantity,
          settlementOverride: settlementOverride
        }));

        var originalAmount = settlementOverride ? settlementOverride.amount : item.left_hand;

        var settlementVisibility = this.budget.settlement_visibilities
          .find((o) => {
            return (
              o.settleable_id === item.overridable_id
                && o.settleable_type === item.overridable_type
                && o.section === item.section
                && amountsMatch(o.amount, originalAmount)
            );
          });

          settlementVisibility && (item = Object.assign({}, item, {
            settlementVisibility: settlementVisibility
          }));
      }

      return item;
    })
  }

  calculateTicketsToBreakeven() {
    var totalExpenses = (
      this.showSummary.artistGuarantee
        + this.expensesSection.total
        + this.finalExpensesSection.total
        + this.additionsSection.total
    );

    var totalRevenue = this.ticketsSection.total;
    var totalTickets = this.ticketsSection.items
      .filter((item) => item.calculatedLeftHand > 0.00)
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedRightHand;
      }, 0);

    var weightedAverageTicketPrice;
    var averagedBreakeven;

    if(totalTickets > 0 && totalRevenue > 0){
      weightedAverageTicketPrice = totalRevenue / totalTickets;
      averagedBreakeven = Math.ceil(totalExpenses / weightedAverageTicketPrice);
    } else {
      weightedAverageTicketPrice = undefined;
      averagedBreakeven = undefined;
    }

    return {
      totalExpenses: totalExpenses,
      totalRevenue: totalRevenue,
      totalTickets: totalTickets,
      weightedAverageTicketPrice: weightedAverageTicketPrice,
      averagedBreakeven: averagedBreakeven
    }
  }

  calculateTicketsToBreakevenForecast() {
    var totalExpenses = (
      this.showSummary.artistGuarantee
        + this.expensesSection.externalForecastTotal
        + this.finalExpensesSection.externalForecastTotal
        + this.additionsSection.externalForecastTotal
    );

    var totalRevenue = this.ticketsSection.externalForecastTotal;
    var totalTickets = this.ticketsSection.items
      .filter((item) => item.calculatedLeftHandForecast > 0.00)
      .filter((item) => this.shouldShow(item))
      .reduce((summed, item) => {
        return summed + item.calculatedRightHandForecast;
      }, 0);

    var weightedAverageTicketPrice;
    var averagedBreakeven;

    if(totalTickets > 0 && totalRevenue > 0){
      weightedAverageTicketPrice = totalRevenue / totalTickets;
      averagedBreakeven = Math.ceil(totalExpenses / weightedAverageTicketPrice);
    } else {
      weightedAverageTicketPrice = undefined;
      averagedBreakeven = undefined;
    }

    return {
      totalExpenses: totalExpenses,
      totalRevenue: totalRevenue,
      totalTickets: totalTickets,
      weightedAverageTicketPrice: weightedAverageTicketPrice,
      averagedBreakeven: averagedBreakeven
    }
  }

  grossPotential() {
    return (this.ticketsSection?.total || 0) + (this.revenueSection?.total || 0);
  }

  grossPotentialForMode(mode) {
    if (mode === 'forecast') {
      return (this.ticketsSection?.externalForecastTotal || 0) + (this.revenueSection?.externalForecastTotal || 0);
    }
    return this.grossPotential();
  }

  getDeductedBeforeSplitTotal(mode) {
    var items = (this.expensesSection?.items || [])
      .concat(this.finalExpensesSection?.items || []);

    return items.reduce((total, item) => {
      if (!item.deducted_before_split || !this.shouldShow(item)) return total;
      var value = mode === 'forecast'
        ? (item.calculatedForecastTotal || 0)
        : (item.calculatedTotal || 0);
      return total + value;
    }, 0);
  }

  // O-4892: the Manual Split Point, read off the offer exactly as
  // FinancialSummaryMath does. Zero is treated as unset — the Simple editor
  // persisted a blank split point as 0 (O-4981).
  // Unlike FinancialSummaryMath#parseMaskedInput, this engine's returns NaN on a
  // blank or masked value instead of zero, and NaN silently zeroes a payout
  // rather than raising — so it is normalised to "unset" here, once.
  breakevenThreshold() {
    if (this.offer.breakeven_threshold == null) return null;
    var threshold = this.parseMaskedInput(this.offer.breakeven_threshold);
    return isNaN(threshold) ? null : threshold;
  }

  applyBreakevenGate(baseAmount) {
    var threshold = this.breakevenThreshold();
    if (threshold == null) return baseAmount;
    return Math.max(0, baseAmount - threshold);
  }

  // The bracketed deal absorbs the split point proportionally: every bracket's
  // revenue is scaled by the share of aggregate revenue left above the split
  // point. Mirrors FinancialSummaryMath's `breakevenScale`.
  breakevenScaleFor(aggregateBracketRevenue) {
    var threshold = this.breakevenThreshold();
    if (threshold == null || aggregateBracketRevenue === 0) return 1.00;
    return Math.max(0, aggregateBracketRevenue - threshold) / aggregateBracketRevenue;
  }

  getRevenueBase(mode) {
    var revenueBase = this.offer.revenue_base || 'default';

    if (revenueBase === 'default') return null;

    if (revenueBase === 'gross') {
      return this.grossPotentialForMode(mode);
    }

    var netBORKey = mode === 'forecast'
      ? 'netBoxOfficeReceiptsForecast'
      : 'netBoxOfficeReceipts';
    var base = (this.showSummary && this.showSummary[netBORKey]) || 0;

    if (revenueBase === 'adjusted_gross') {
      base = base - this.getDeductedBeforeSplitTotal(mode);
    }

    return base;
  }
}
